/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 **/
import { INVENTORY_LOOKUP_BFF_PATH } from '../constants'
import { BFF_BASE_URL } from '../environment'
import { appApiBff } from '../http-common'
import { Item, APIStore } from '../types'

import OAuthService from './OAuthService'

class ItemLookupDataService extends OAuthService {
  /**
   * Searches for items by term in a specific store
   * @param storeNumber - The store identifier
   * @param searchTerm - The search query
   * @param config - Optional request configuration
   * @returns Promise resolving to array of items
   */
  async findAllByTerm(storeNumber: string, searchTerm: string, config?: {}) {
    if (!storeNumber?.trim()) {
      throw new Error('Store number is required')
    }
    if (!searchTerm?.trim()) {
      throw new Error('Search term is required')
    }
    const url = new URL(`${BFF_BASE_URL}${INVENTORY_LOOKUP_BFF_PATH}/${storeNumber}/items`)
    url.searchParams.set('searchTerm', searchTerm)

    return appApiBff.get<Array<Item>>(url.toString(), {
      ...config,
      headers: this.getHeaders()
    })
  }

  /**
   * Finds the nearest store based on geographic coordinates
   * @param latitude - Geographic latitude (-90 to 90)
   * @param longitude - Geographic longitude (-180 to 180)
   * @param config - Optional request configuration
   * @returns Promise resolving to store information
   */
  async getStoreByLocation(latitude: number, longitude: number, config?: {}) {
    const url = new URL(`${BFF_BASE_URL}${INVENTORY_LOOKUP_BFF_PATH}`)
    url.searchParams.set('latitude', latitude.toString())
    url.searchParams.set('longitude', longitude.toString())

    return appApiBff.get<APIStore>(url.toString(), {
      ...config,
      headers: this.getHeaders()
    })
  }
}

export default new ItemLookupDataService()
